import React, { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { LogOut, FileText, PlusSquare } from 'lucide-react';
import axios from 'axios';
import App from './App';
import './App.css';

interface Document {
  id: number;
  filename: string;
  uploaded_at: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const Sidebar: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  documents: Document[];
  onSelectDocument: (doc: Document) => void;
  selectedDocument: Document | null;
  onNewChat: () => void;
}> = ({ isOpen, onClose, documents, onSelectDocument, selectedDocument, onNewChat }) => {
  const { keycloak } = useKeycloak();
  const username = keycloak.tokenParsed?.preferred_username || 'Usuario';
  return (
    <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
      <div className="sidebar-header">
        <h2 className="sidebar-title">DocuChat AI</h2>
      </div>
      <div className="sidebar-content">
        <nav>
          <ul className="sidebar-menu">
            <li className="sidebar-menu-item-header">
              <span>Documentos</span>
              <button
                onClick={onNewChat}
                className="new-chat-button"
                title="Nuevo chat"
              >
                <PlusSquare size={16} />
              </button>
            </li>
            {documents.length > 0 ? (
              documents.map((doc, index) => (
                <li
                  key={doc.id}
                  onClick={() => onSelectDocument(doc)}
                  className={selectedDocument?.id === doc.id ? 'selected' : ''}
                >
                  <FileText size={16} />
                  <span>{doc.filename}</span>
                </li>
              ))
            ) : (
              <li className="no-documents-message">
                <span>No hay documentos cargados.</span>
              </li>
            )}
          </ul>
        </nav>
      </div>
      <div className="sidebar-footer">
        <div className="user-info sidebar-user-info">
          <span className="username">Hola, {username}</span>
          <button
            onClick={() => keycloak.logout()}
            className="action-button logout-button"
            title="Cerrar Sesión"
          >
            <LogOut size={20} />
          </button>
        </div>
      </div>
    </aside>
  );
};

const Layout: React.FC = () => {
  const { keycloak, initialized } = useKeycloak();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const refreshDocuments = async (selectNewDocument: boolean = false) => {
    if (initialized && keycloak.authenticated && keycloak.token) {
      try {
        const response = await axios.get(`${API_URL}/history`, {
          headers: {
            Authorization: `Bearer ${keycloak.token}`,
          },
        });
        setDocuments(response.data);
        
        if (selectNewDocument && response.data.length > 0) {
          setSelectedDocument(response.data[0]);
        }
      } catch (error) {
        console.error("Error al obtener los documentos:", error);
      }
    }
  };

  const handleNewDocumentUploaded = () => {
    refreshDocuments(true);
  };

  const handleNewChat = () => {
    setSelectedDocument(null);
    refreshDocuments();
  };

  useEffect(() => {
    refreshDocuments();
  }, [initialized, keycloak.authenticated, keycloak.token]);

  if (!initialized) {
    return <div className="loading-screen">Cargando...</div>;
  }

  if (!keycloak.authenticated) {
    return (
      <div className="layout-container">
        <div className="chat-container">
          <header className="chat-header">
            <h1>DocuChat AI</h1>
          </header>
          <div className="chat-messages">
            <div className="message-bubble bot-message">
              <p>Por favor, inicia sesión para acceder a DocuChat AI.</p>
              <button onClick={() => keycloak.login()} className="action-button login-button">
                Iniciar Sesión
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="layout-container">
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={toggleSidebar}
        documents={documents}
        onSelectDocument={setSelectedDocument}
        selectedDocument={selectedDocument}
        onNewChat={handleNewChat}
      />
      {isSidebarOpen && <div className="sidebar-overlay" onClick={toggleSidebar}></div>}
      <div className="chat-main-content">
        <App
          onSidebarToggle={toggleSidebar}
          selectedDocument={selectedDocument}
          setSelectedDocument={setSelectedDocument}
          onNewDocumentUploaded={handleNewDocumentUploaded}
        />
      </div>
    </div>
  );
};

export default Layout;